import React, { FC, Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSearchProduct } from "../../../api/queries/productQueries";
import { AdminConfigButton } from "../../../components/admin-mode/AdminConfigButton";
import { AdminDragdropArea } from "../../../components/admin-mode/AdminDragdropArea";
import { AdminDragdropItem } from "../../../components/admin-mode/AdminDragdropItem";
import ProdCard from "../../../components/box/ProdCard";
import { ProdCardOfTamiceChoiceBox } from "../../../components/box/ProdCardOfTamiceChoiceBox";
import { TicketBox } from "../../../components/box/TicketBox";
import { BtnOutlineWhite } from "../../../components/button/BtnOutlineWhite";
import NetworkMediaFile from "../../../components/image/NetworkMediaFile";
import { UiTitle } from "../../../components/text/UiTitle";
import { useNaviProduct } from "../../../hooks/useNaviProduct";
import { useResponsive } from "../../../hooks/useResponsive";
import { useTranslationEx } from "../../../hooks/useTranslationEx";
import { useAdminMode } from "../../../redux/slices/adminSlice";
import CategoryService from "../../../services/categoryService";
import ProductService, {
  updateOrderNoOfProduct,
} from "../../../services/productService";
import { TicketBoxSwiper } from "../../HomePage/components/RecommendTicketSwiper";
import { LazySectionEx } from "../../../components/LazySection";
import { LoadingProductCard } from "../../../components/LoadingCard";
import { icAsset } from "../../../components/image/image-src";
import clsx from "clsx";

const indexOffset = 1000000; // to avoid conflict with index in categories

const ListView: React.FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  return (
    <div className="ui-container mb-[2.5rem] mt-[2rem] flex flex-col gap-[2rem] sm:mt-[5rem] sm:gap-[5rem]">
      <TtProductType dto={dto} />
      <div className="flex w-full flex-col gap-8 sm:grid sm:grid-cols-2 sm:gap-6">
        {/* flex w-full flex-col gap-8 sm:flex-row sm:justify-between sm:gap-6 */}
        <TamiceDealDesktop dto={dto} />
        <TamiceDealMobile dto={dto} />
        <ThisMonthTopNews dto={dto} />
      </div>
      <TheBestInCity dto={dto} />
      <TamiceChoice dto={dto} />
    </div>
  );
};

const TamiceDealMobile: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const responsive = useResponsive();
  const { t } = useTranslation();
  const ls = dto.tamiceDeals;
  if (!ls.length || responsive.isDesktop) return null;
  return (
    <section>
      <UiTitle className="mb-3" text={t("Tamice’s Deal")} />
      <TicketBoxSwiper products={ls} />
    </section>
  );
};

const TamiceDealDesktop: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const responsive = useResponsive();
  const max = 3;
  const { t } = useTranslation();
  const ls = dto.tamiceDeals.slice(0, max);
  const { t2 } = useTranslationEx();
  if (!ls.length || !responsive.isDesktop) return null;
  return (
    <section className="w-full">
      <UiTitle text={t("Tamice’s Deal")} className="mb-3" />
      <div className="tamice-deals" data-num={ls.length}>
        {ls.map((product) => (
          <TicketBox
            key={product.id}
            product={{
              ...product,
              name: t2(product.name),
            }}
            cdnParams={{
              format: "webp",
              width: 700,
              height: 300,
              fit: "cover",
            }}
          />
        ))}
      </div>
    </section>
  );
};

const TamiceChoice: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const { t } = useTranslation();
  const ls = dto.tamiceChoices;
  if (!ls.length) return null;
  const numberOfSlide = Math.ceil(ls.length / 4);
  return (
    <section>
      <UiTitle className="mb-3" text={t("Tamice’s Choice")} />
      <Swiper
        className="default-swiper blue-dot swiper-button-bottom-0 relative h-full overflow-x-hidden overflow-y-hidden"
        spaceBetween={16}
        slidesPerView={1}
        modules={[Navigation, Pagination]}
        pagination={true}
        style={
          {
            "--swiper-button-bottom": "-10px",
          } as React.CSSProperties & Record<string, any>
        }
      >
        {Array.from(Array(numberOfSlide).keys()).map((slide) => {
          const tmp = ls.slice(slide * 4, (slide + 1) * 4);
          return (
            <SwiperSlide key={tmp.map((x) => x.id).join(",")}>
              <div
                className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2"
                data-num={ls.length}
              >
                {tmp.map((product) => (
                  <ProdCardOfTamiceChoiceBox key={product.id} {...product} />
                ))}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

export const ThisMonthTopNews: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const { t } = useTranslation();

  const navi = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  if (!dto.news?.length) return null;
  return (
    <section
      role="button"
      onClick={() => {
        const id = dto.news[currentSlide].id;
        navi("/cities/" + dto.city.slug + "/news/" + id);
      }}
    >
      <UiTitle text={t("This month top news")} className="mb-3" />
      <div className="relative h-[12.5rem] sm:h-[30rem] sm:max-w-[40.8125rem]">
        <Swiper
          className="default-swiper relative h-full"
          spaceBetween={8}
          slidesPerView={1}
          modules={[Navigation, Pagination]}
          pagination={true}
          onSlideChange={(swiper: any) => setCurrentSlide(swiper.activeIndex)}
        >
          {dto.news.map((news, index) => {
            return (
              <SwiperSlide
                className="h-[12.5rem] sm:h-[46.25rem]"
                key={news.id}
              >
                <div className="relative h-[12.5rem] overflow-hidden rounded-[15px] sm:h-[30rem]">
                  <NetworkMediaFile
                    className="h-full w-full"
                    file={{
                      src: news.thumbnail ?? "",
                    }}
                    cdnParams={{
                      format: "webp",
                      width: 700,
                      height: 700,
                      fit: "cover",
                    }}
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 h-[30rem] bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.6)_42.05%,_rgba(0,_0,_0,_0)_100%)] p-8">
                    <p className="font-semibold text-[#FFFFFF] sm:text-[1.25rem] sm:leading-[1.625rem]">
                      {t(news.title)}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
        <div className="absolute bottom-0 right-0 z-30 pb-6 pr-6">
          <BtnOutlineWhite>{t("Explore now")}</BtnOutlineWhite>
        </div>
      </div>
    </section>
  );
};

const TheBestInCity: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const { t } = useTranslation();
  const [activeCate, setActiveCateId] = useState({
    id: 0,
    name: t("All"),
  });
  const { isAdminMode } = useAdminMode();
  const [categories, setCategories] = useState<CategoriesDTO[]>([]);
  const [categoriesSave, setCategoriesSaved] = useState<CategoriesDTO[]>([]);
  useEffect(() => {
    if (dto?.sortedCategories?.length) {
      setCategories(dto.sortedCategories);
      setCategoriesSaved(dto.sortedCategories);
    }
  }, [dto?.sortedCategories]);
  if (!categories.length) return null;

  const saveOrder = async () => {
    const ids = categories
      .map((c) => ({ id: c?.id }))
      .filter((c) => c.id !== 0);
    await CategoryService.updateOrderNo(ids);
    setCategoriesSaved(categories);
  };

  const isOrderChange: boolean = isAdminMode
    ? !!categoriesSave.length &&
      categories.some((c, i) => c.id !== categoriesSave[i]?.id)
    : false;
  return (
    <div className="w-full">
      <UiTitle className="mb-3" text={t("With Tamice") + "!"} />
      <AdminConfigButton />
      {isAdminMode && isOrderChange && (
        <TtSaveBtn
          title={t("Save category order")}
          saveOrder={() => {
            saveOrder();
          }}
        />
      )}

      <div className="mb-10">
        {isAdminMode ? (
          <AdminDragdropArea isAdminMode={isAdminMode}>
            <div className="flex w-full flex-wrap gap-8">
              <div className="!w-fit">
                <button
                  type="button"
                  className={clsx(
                    "inline-flex h-[48px] items-center justify-center gap-[16px] text-lg font-semibold leading-[25px] text-[#232323]",
                    activeCate?.id === 0
                      ? "[border-bottom:2px_solid_#232323]"
                      : "",
                  )}
                  onClick={() => {
                    setActiveCateId({ id: 0, name: t("All") });
                  }}
                >
                  {t("All")}
                </button>
              </div>
              {categories.flatMap((cate: CategoriesDTO, index: number) => {
                return (
                  <AdminDragdropItem
                    type="category"
                    key={cate?.id}
                    isAdminMode={isAdminMode}
                    index={index}
                    moveItem={(dragIndex: number, hoverIndex: number) => {
                      if (dragIndex >= indexOffset || hoverIndex >= indexOffset)
                        return;
                      const newCategories = [...categories];
                      const [movedItem] = newCategories.splice(dragIndex, 1);
                      newCategories.splice(hoverIndex, 0, movedItem);
                      setCategories(newCategories);
                    }}
                    onDrop={() => {
                      // const ids = categories
                      //   .map((c) => ({ id: c?.id }))
                      //   .filter((c) => c.id !== 0);
                      // CategoryService.updateOrderNo(ids);
                    }}
                  >
                    <div key={cate.id} className="!w-fit">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCateId(cate);
                        }}
                        className={
                          "inline-flex h-[48px] items-center justify-center gap-[16px] text-lg font-semibold leading-[25px] text-[#232323] " +
                          (activeCate?.id === cate.id
                            ? "[border-bottom:2px_solid_#232323]"
                            : "")
                        }
                      >
                        {t(cate.name)}
                      </button>
                    </div>
                  </AdminDragdropItem>
                );
              })}
            </div>
          </AdminDragdropArea>
        ) : (
          <Swiper className="w-full" spaceBetween={32} slidesPerView={"auto"}>
            {[{ id: 0, name: t("All") }, ...dto.sortedCategories].flatMap(
              (cate: CategoriesDTO) => {
                return (
                  <SwiperSlide key={cate.id} className="!w-fit">
                    <button
                      onClick={() => {
                        setActiveCateId(cate);
                      }}
                      type="button"
                      className={
                        "inline-flex h-[48px] items-center justify-center gap-[16px] text-lg font-semibold leading-[25px] text-[#232323] " +
                        (activeCate?.id === cate.id
                          ? "[border-bottom:2px_solid_#232323]"
                          : "")
                      }
                    >
                      {t(cate.name)}
                    </button>
                  </SwiperSlide>
                );
              },
            )}
          </Swiper>
        )}
      </div>
      <div>
        <CategoriesContent dto={dto} activeCate={activeCate} />
      </div>
    </div>
  );
};

export const CategoriesContent: FC<{
  dto: CityPageResponseDTO;
  activeCate: CategoriesDTO;
}> = ({ dto, activeCate }) => {
  const { isAdminMode } = useAdminMode();
  const orderingKey = "city" + dto.city?.id + "-cate" + (activeCate.id || 0);
  const { data, fetchNextPage, hasNextPage } = useSearchProduct({
    orderingKey,
    city: dto.city.id,
    // category: activeCate?.parent ? null : activeCate.id,
    // subCategory: activeCate?.parent ? activeCate.id : null,
    subCategory: activeCate.id || null,
    pageSize: isAdminMode ? 999999 : 8,
  });
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductDetailResponse[]>([]);
  const [saveProducts, setSaveProducts] = useState<ProductDetailResponse[]>();
  useEffect(() => {
    if (data?.pages) {
      const allProducts = data.pages.flatMap((page) => page.data);
      setProducts(allProducts);
      setSaveProducts(allProducts);
    }
  }, [data]);

  const saveOrder = async () => {
    const ids = products.map((p) => p.id);
    await updateOrderNoOfProduct(orderingKey, {
      ids,
      offset: +(dto.city?.id ?? 0) * 1000,
    });
    setSaveProducts(products);
  };
  const isOrderChange: boolean = isAdminMode
    ? products.some((p, i) => p.id !== saveProducts?.[i]?.id)
    : false;

  const max = 4;
  const groups: ProductDetailResponse[][] = [];
  const tmpProducts = [...products];
  for (let i = 0; i < tmpProducts.length; i += max) {
    groups.push(tmpProducts.slice(i, i + max));
  }

  return (
    <>
      {isAdminMode && isOrderChange && (
        <TtSaveBtn
          title={t("Save product order")}
          saveOrder={() => {
            saveOrder();
          }}
        />
      )}
      <AdminDragdropArea isAdminMode={isAdminMode}>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {isAdminMode
            ? groups
                .flat()
                .map((product: ProductDetailResponse, index: number) => {
                  return (
                    <AdminDragdropItem
                      type="product"
                      key={product?.id}
                      isAdminMode={isAdminMode}
                      index={index + indexOffset}
                      moveItem={(dragIndex: number, hoverIndex: number) => {
                        const localDragIndex = dragIndex - indexOffset;
                        const localHoverIndex = hoverIndex - indexOffset;

                        // Nếu index không hợp lệ thì bỏ qua
                        if (localDragIndex < 0 || localHoverIndex < 0) return;

                        setProducts((prevProducts) => {
                          const updated = [...prevProducts];
                          const [movedItem] = updated.splice(localDragIndex, 1);
                          updated.splice(localHoverIndex, 0, movedItem);
                          return updated;
                        });
                      }}
                      onDrop={() => {
                        // const ids = products.map((p) => p.id);
                        // updateOrderNoOfProduct({
                        //   ids,
                        //   offset: +(dto.city?.id ?? 0) * 1000,
                        // });
                      }}
                    >
                      <div className="white-shaddow-15" key={product?.id}>
                        <ProdCard
                          {...product}
                          readonly={isAdminMode}
                          loading="eager"
                        />
                      </div>
                    </AdminDragdropItem>
                  );
                })
            : groups.map((group) => {
                const keys = group.map((product) => product.id).join(",");
                return (
                  <LazySectionEx
                    fallback={
                      <Fragment>
                        {Array.from({ length: group.length }).map((_, idx) => (
                          <LoadingProductCard key={idx} />
                        ))}
                      </Fragment>
                    }
                    className=""
                    key={keys}
                  >
                    {group.map((product, index) => (
                      <div className="white-shaddow-15" key={product?.id}>
                        <ProdCard
                          {...product}
                          readonly={isAdminMode}
                          loading="eager"
                        />
                      </div>
                    ))}
                  </LazySectionEx>
                );
              })}
        </div>
      </AdminDragdropArea>
      {hasNextPage && (
        <div className="mt-10 flex w-full justify-center">
          <button
            className="flex h-[40px] w-[120px] items-center justify-center rounded-[55px] border-[1px] border-solid border-[#232323] px-[16px] py-[0] text-sm font-semibold leading-[20px] text-[#232323]"
            type="button"
            onClick={() => fetchNextPage()}
          >
            {t("Load more")}
          </button>
        </div>
      )}
    </>
  );
};
export const ProductListView: FC<{
  cityId: number;
  productType: string;
}> = ({ cityId, productType }) => {
  const { data: productsData } = useQuery({
    queryKey: ["product", productType, cityId],
    queryFn: () =>
      ProductService.getProductCity(Number(cityId), productType || undefined),
    enabled: !!cityId,
  });

  return (
    <div className="grid grid-cols-4 gap-x-6 gap-y-[1.875rem]">
      {productsData?.product?.map((p) => <ProdCard {...p} />)}
    </div>
  );
};

const TtProductType: FC<{ dto: CityPageResponseDTO }> = ({ dto }) => {
  const { t } = useTranslation();
  const productTypes: {
    urlIcon: string;
    name: string;
    productType: TProductType;
    href: string;
  }[] = [
    {
      urlIcon: "../assets/images/icons/bigpass.svg",
      name: t("Big Pass"),
      productType: "PASS",
      href: "/pass?city=",
    },
    {
      urlIcon: "../assets/images/icons/ticket.svg",
      name: t("Ticket"),
      productType: "TICKET",
      href: "/tickets?city=",
    },
    {
      urlIcon: "../assets/images/icons/air-balloon.svg",
      name: t("Tour"),
      productType: "TOUR",
      href: "/guide-tours?city=",
    },
    {
      urlIcon: "../assets/images/icons/watch.svg",
      name: t("Musical"),
      productType: "MUSICAL",
      href: "/musicals?city=",
    },
    {
      urlIcon: "../assets/images/icons/sim-card.svg",
      name: t("E-sim"),
      productType: "SIM",
      href: "/e-sims?city=",
    },
  ];

  const navi = useNavigate();
  const { naviProduct } = useNaviProduct();
  return (
    <section className="grid min-h-[80px] grid-cols-3 justify-center gap-x-[0.75rem] gap-y-4 sm:flex sm:min-h-[120px] sm:gap-x-[2.875rem] sm:gap-y-[2.875rem]">
      {productTypes
        .filter((x) => dto.existsProductType[x.productType])
        .map((x) => {
          return (
            <button
              onClick={() => {
                if (x.productType === "PASS") {
                  naviProduct({
                    productType: x.productType,
                    slug: dto.passProduct?.slug!,
                  });
                  return;
                }
                navi(x.href + dto.city.id);
              }}
              key={x.productType}
              className={
                "group h-[5rem] items-center gap-2 rounded-2xl border border-[#E0E0E0] bg-white shadow-custom3 hover:border-primary-light sm:h-[7.5rem] sm:w-[10rem] sm:gap-[0.625rem] sm:px-4"
              }
            >
              <img
                className="mx-auto size-8 transition-transform duration-300 group-hover:-translate-y-2 sm:size-11"
                src={icAsset(x.urlIcon)}
                alt={x.name}
              />
              <span className="text-xs font-normal text-[#5E6D77] sm:text-[0.875rem]">
                {x.name}
              </span>
            </button>
          );
        })}
    </section>
  );
};

export default ListView;
export const TtSaveBtn: React.FC<{
  title: string;
  saveOrder: () => void;
}> = ({ title, saveOrder }) => {
  return createPortal(
    <button
      type="button"
      onClick={saveOrder}
      className="inline-flex w-[220px] items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 px-4 py-2 text-white shadow-lg transition hover:scale-105 hover:from-blue-600 hover:to-purple-600"
    >
      <svg
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      {title}
    </button>,
    document.body.querySelector(".admin-config-area")!,
  );
};
